import React, { useRef, useState } from 'react';
import axios from 'axios';
import { Loader2, Upload, Video, X } from 'lucide-react';
import { formatErr, notifyErr } from './constants';
import { API } from '../../../lib/api';

// Upload widget + thumbnail gallery for inspection photos/videos.
// `stage` tags new uploads as "inspection" (default) or "completion" so the
// same record can carry both incoming-vehicle and handover media. The gallery
// is filtered client-side by the same stage.
// For Cloudinary-stored media we render the public secure_url directly to
// avoid the auth-required redirect endpoint (which `<img src>` can't sign).
const MediaPanel = ({ inspectionId, media, authHeader, onChanged, stage = 'inspection', uploadLabel }) => {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [err, setErr] = useState('');

  const onPick = () => inputRef.current?.click();

  const onFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    if (!files.length) return;
    setErr('');
    setUploading(true);
    try {
      const fd = new FormData();
      files.forEach((f) => fd.append('files', f));
      fd.append('stage', stage);
      await axios.post(`${API}/inspections/${inspectionId}/media`, fd, {
        headers: { ...authHeader(), 'Content-Type': 'multipart/form-data' },
        timeout: 5 * 60 * 1000, // 5 minutes for large media uploads
        maxContentLength: 100 * 1024 * 1024,
        maxBodyLength: 100 * 1024 * 1024,
      });
      onChanged && onChanged();
    } catch (e2) {
      setErr(formatErr(e2));
    } finally {
      setUploading(false);
    }
  };

  const onDelete = async (fileId) => {
    if (!window.confirm('Delete this attachment?')) return;
    try {
      await axios.delete(`${API}/inspections/${inspectionId}/media/${fileId}`, {
        headers: authHeader(),
      });
      onChanged && onChanged();
    } catch (e) {
      notifyErr(e);
    }
  };

  // For Cloudinary-stored media, prefer the public CDN URL directly — using
  // the auth-gated API endpoint with `<img src>`/`<video src>` causes the
  // browser to block the cross-origin redirect (ERR_BLOCKED_BY_ORB) because
  // those tags can't send the Authorization header. The Cloudinary URL is a
  // signed/secure public link and works without auth.
  const mediaUrl = (m) =>
    m.secure_url || `${API}/inspections/${inspectionId}/media/${m.id}`;

  // Filter to current stage. Media uploaded before the field was introduced
  // is treated as `inspection` for backward compatibility.
  const visibleMedia = (media || []).filter(
    (m) => (m.stage || 'inspection') === stage,
  );

  return (
    <div>
      <input
        ref={inputRef}
        type="file"
        multiple
        accept="image/*,video/*"
        onChange={onFiles}
        className="hidden"
        data-testid={`media-file-input-${stage}`}
      />
      <div className="flex flex-wrap gap-3 items-start">
        <button
          type="button"
          onClick={onPick}
          disabled={uploading}
          className="inline-flex items-center gap-2 bg-teal-500/15 hover:bg-teal-500/25 border border-teal-400/40 text-teal-700 text-sm px-4 py-2.5 rounded-lg transition disabled:opacity-60"
          data-testid={`media-upload-btn-${stage}`}
        >
          {uploading ? <Loader2 className="animate-spin" size={14} /> : <Upload size={14} />}
          {uploading ? 'Uploading…' : (uploadLabel || 'Add photos / videos')}
        </button>
        <span className="text-xs text-slate-500 self-center">
          Up to 50 MB per file. JPG, PNG, MP4, MOV supported.
        </span>
      </div>
      {err && (
        <div className="mt-2 text-sm text-red-300 bg-red-500/10 border border-red-500/30 rounded px-3 py-2" data-testid={`media-error-${stage}`}>
          {err}
        </div>
      )}
      {visibleMedia.length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-4" data-testid={`media-grid-${stage}`}>
          {visibleMedia.map((m) => (
            <div key={m.id} className="relative group bg-slate-900/60 border border-white/10 rounded-lg overflow-hidden" data-testid={`media-${m.id}`}>
              {m.kind === 'video' ? (
                <div className="aspect-video flex flex-col items-center justify-center text-slate-400 p-2">
                  <Video size={24} className="text-teal-300" />
                  <span className="text-xs mt-1.5 truncate w-full text-center">{m.original_name || m.filename}</span>
                  <a
                    href={mediaUrl(m)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs text-teal-300 hover:underline mt-1"
                  >open</a>
                </div>
              ) : (
                <a href={mediaUrl(m)} target="_blank" rel="noopener noreferrer">
                  <img
                    src={mediaUrl(m)}
                    alt={m.original_name}
                    className="w-full aspect-video object-cover"
                  />
                </a>
              )}
              <button
                type="button"
                onClick={() => onDelete(m.id)}
                className="absolute top-1.5 right-1.5 bg-black/70 hover:bg-red-500/80 text-white w-7 h-7 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition"
                data-testid={`media-delete-${m.id}`}
                title="Delete"
              >
                <X size={14} />
              </button>
              <div className="absolute bottom-1.5 left-1.5 bg-black/70 text-white text-[10px] px-1.5 py-0.5 rounded uppercase font-mono">
                {m.kind}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default MediaPanel;
